import React, { useEffect, useRef, useState } from 'react';
import { Alert, App, Button, Descriptions, Modal, Space, Spin, Table } from 'antd';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import ManualPZInvoiceLink from './ManualPZInvoiceLink';
import type { WorkflowInvoice } from '../../../types/workflowInvoice';
import { formatUnit } from '../../../utils/units';
import { useFeedback } from '../../../utils/feedback';
import { getInvoiceImportErrorMessage } from '../invoiceImportErrorMessages';
import { submitWithOperationalException } from '../../operationalExceptions/operationalExceptionFlow';
import {
  fetchPurchaseReceiptPreview,
  receivePurchaseInvoice,
  type InvoiceApprovalPreview,
  type PendingReceipt,
  type PurchaseReceiptLine,
} from '../api/invoiceWorkflowApi';

type PzDocument = { id: string; pzNumber: string };

/**
 * WP7 (decision 2026-09-24-wp7-pz-po-fakturze): right after a purchase
 * invoice approval, offer "Utworzyć PZ automatycznie?". This is only a UX
 * layer on the existing receipt command (`POST /purchase-invoices/:id/receive`)
 * — the payload is the one `PurchaseInvoiceReceipt` would send with every
 * remaining invoice quantity prefilled. Anything that needs an operator choice
 * (manual PZ awaiting an invoice, earlier WZ issues to match, a rejected
 * command) never guesses: it links instead, or hands over to the receipt panel.
 */
export default function PurchaseReceiptPrompt({ open, invoice, preview, previewFailed, onClose, onReceived, onLinked, onFallback }: {
  open: boolean;
  invoice: WorkflowInvoice;
  preview: InvoiceApprovalPreview | null;
  previewFailed: boolean;
  onClose: () => void;
  onReceived: (documents: PzDocument[]) => void;
  onLinked: () => void;
  onFallback: (message: string) => void;
}) {
  const { t } = useTranslation(['invoices', 'common']);
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const text = (name: string, params?: Record<string, unknown>) => t(`invoices:workflow.receiptPrompt.${name}`, params);
  const [lines, setLines] = useState<PurchaseReceiptLine[] | null>(null);
  const [pendingPZs, setPendingPZs] = useState<PendingReceipt[]>([]);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<PzDocument[] | null>(null);
  const key = useRef<string | null>(null);

  useEffect(() => {
    if (!open || !invoice.id) return;
    let active = true;
    setLines(null); setPendingPZs([]); setCreated(null); key.current = null;
    fetchPurchaseReceiptPreview(invoice.id)
      .then(result => { if (active) { setLines(result.lines); setPendingPZs(result.pendingPZs || []); } })
      .catch(() => { if (active) onFallback(text('loadFailed')); });
    return () => { active = false; };
    // Load once per opening; callbacks/i18n identities must not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, invoice.id]);

  const remaining = (lines || []).filter(line => line.remainingQuantity > 0);
  const needsSelection = Boolean(preview?.pendingStockCandidates.some(group => group.candidates.length > 0));
  const ready = lines !== null && (preview !== null || previewFailed);
  const zdNumbers = (preview?.orders || []).map(order => order.orderNumber).filter(Boolean).join(', ');

  const create = async () => {
    if (!invoice.id || !invoice.version || busy) return;
    setBusy(true);
    key.current ??= `receipt-${invoice.id}-${Date.now()}`;
    try {
      // Same command and payload shape as the receipt panel: one line per
      // remaining invoice quantity, no WZ matching, no new-delivery override.
      const input = {
        confirmNewDelivery: false,
        expectedVersion: invoice.version,
        idempotencyKey: key.current,
        recognitionMonth: dayjs().format('YYYY-MM'),
        pendingStockSelections: [],
        receiptQuantities: remaining.map(line => ({ purchaseInvoiceItemId: line.purchaseInvoiceItemId, quantity: line.remainingQuantity })),
      };
      const result = await submitWithOperationalException(payload => receivePurchaseInvoice(invoice.id!, payload as typeof input), input, modal, t);
      if (!result) return;
      key.current = null;
      setCreated(result.pzDocuments);
      onReceived(result.pzDocuments);
      feedback.success(text('created', { numbers: result.pzDocuments.map(document => document.pzNumber).join(', ') }));
    } catch (cause) {
      onFallback(`${text('fallback')} ${getInvoiceImportErrorMessage(cause, t, 'invoices:workflow.physicalReceipt.failed')}`);
    } finally { setBusy(false); }
  };

  const later = <Button key="later" disabled={busy} onClick={onClose}>{text('later')}</Button>;
  let body: React.ReactNode;
  let footer: React.ReactNode[] = [later];
  if (created) {
    body = <Space direction="vertical" style={{ width: '100%' }}>
      <Alert type="success" showIcon message={text('createdTitle')} />
      {created.map(document => <Link key={document.id} to={`/app/pz/${document.id}`} data-testid="receipt-prompt-pz-link">{text('openPz', { number: document.pzNumber })}</Link>)}
    </Space>;
    footer = [<Button key="close" type="primary" onClick={onClose}>{t('common:close')}</Button>];
  } else if (!ready) body = <Spin />;
  else if (pendingPZs.length > 0) {
    // A manual PZ already recorded this delivery: offer linking, never a duplicate.
    body = <Space direction="vertical" style={{ width: '100%' }}>
      <Alert type="warning" showIcon message={text('existingPzTitle')} description={text('existingPzHint')} />
      <ManualPZInvoiceLink invoice={invoice} candidates={pendingPZs} onLinked={onLinked} />
    </Space>;
  } else if (!remaining.length) {
    body = <Alert type="success" showIcon message={t('invoices:workflow.physicalReceipt.complete')} />;
    footer = [<Button key="close" onClick={onClose}>{t('common:close')}</Button>];
  } else if (needsSelection || previewFailed) {
    const message = text(needsSelection ? 'selectionRequired' : 'previewFailed');
    body = <Alert type="info" showIcon message={message} />;
    footer = [later, <Button key="panel" type="primary" onClick={() => onFallback(message)}>{text('openPanel')}</Button>];
  } else {
    body = <Space direction="vertical" style={{ width: '100%' }}>
      <p>{text('hint')}</p>
      <Descriptions size="small" column={1} items={[
        { key: 'zd', label: text('supplierOrder'), children: zdNumbers || '—' },
        { key: 'supplier', label: text('supplier'), children: invoice.sellerName || '—' },
        { key: 'date', label: text('receiptDate'), children: invoice.issueDate ? dayjs(invoice.issueDate).format('DD.MM.YYYY') : '—' },
      ]} />
      <Table<PurchaseReceiptLine> size="small" rowKey="purchaseInvoiceItemId" pagination={false} dataSource={remaining} columns={[
        { title: t('invoices:workflow.physicalReceipt.product'), dataIndex: 'productName' },
        { title: text('quantity'), render: (_, line) => `${line.remainingQuantity} ${formatUnit(line.unit)}` },
      ]} />
    </Space>;
    footer = [later, <Button key="create" type="primary" loading={busy} onClick={() => void create()}>{text('create')}</Button>];
  }

  return <Modal open={open} title={text('title')} onCancel={busy ? undefined : onClose} closable={!busy} maskClosable={false} footer={footer} destroyOnHidden width={640}>
    {body}
  </Modal>;
}
